import React, { useContext } from 'react';
import { css } from 'styled-components';
import { screenMdMax } from '@spotify-internal/encore-web';
const DURATION = '500ms';
export const DELAYS = {
    FIRST: 0,
    SECOND: 250,
    THIRD: 500,
    FOURTH: 750,
    FIFTH: 1000,
    SIXTH: 1250,
    SEVENTH: 1500,
    EIGHTH: 1750,
    NINTH: 2000,
    TENTH: 2250,
};
export const Crossfade = css `
  /* cross-fade */
  opacity: ${({ active }) => (active ? 1 : 0)};
  z-index: ${({ active }) => (active ? 1 : 'unset')};
  transition: visibility 0.75s, opacity 0.75s linear, z-index 0s 0.75s,
    color 0s 0.75s;
`;
const isAnimatable = (mod) => [
    'accordionModule',
    'checklistModule',
    'editorialGridModule',
    'faqLinksModule',
    'featuredContent2Up',
    'featuredContent3up',
    'featuredContentModule',
    'groupTestimonial',
    'headerModule',
    'heroBodyCarousel',
    'heroBodyModule',
    'heroCaseStudyCover',
    'heroHeaderCarousel',
    'heroNarrativeCarousel',
    'heroNarrativeQuoteSlide',
    'heroNarrativeTextSlide',
    'heroNarrativeCover',
    'jumpLinkHeroModule',
    'miniCarouselNarrative',
    'pageBreakerModule',
    'relatedLinksModule',
    'singleFeatureModule',
    'singleTestimonial',
    'videoModule',
    'videoModuleLongBody',
].includes(mod);
const customThresholds = {
    featuredContent2Up: 0.4,
    featuredContent3Up: 0.4,
    groupTestimonial: 0.6,
    editorialGrid: 0.6,
    relatedLinksModule: 0.5,
    singleTestimonial: 0.6,
};
export const customThreshold = (contentType) => {
    if (contentType in customThresholds) {
        return customThresholds[contentType];
    }
    return 0.25;
};
const applyKeyframes = css `
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;
const applyStartState = (props) => {
    var _a, _b, _c;
    return !((_a = props.animationContext) === null || _a === void 0 ? void 0 : _a.isOnScreen) &&
        ((_b = props.animationContext) === null || _b === void 0 ? void 0 : _b.moduleAnimatable) &&
        ((_c = props.animationContext) === null || _c === void 0 ? void 0 : _c.hasAnimated) < 1
        ? `opacity: 0;`
        : '';
};
const applyAnimation = (delay, props) => {
    var _a, _b, _c;
    return ((_a = props.animationContext) === null || _a === void 0 ? void 0 : _a.isOnScreen) &&
        ((_b = props.animationContext) === null || _b === void 0 ? void 0 : _b.moduleAnimatable) &&
        ((_c = props.animationContext) === null || _c === void 0 ? void 0 : _c.hasAnimated) < 2
        ? `animation: ${DURATION} linear ${delay}ms 1 both fadeIn;`
        : '';
};
function useOnScreen(ref, rootMargin = '0px', threshold = 0.25) {
    const [isIntersecting, setIntersecting] = React.useState(false);
    React.useEffect(() => {
        const currentRef = typeof ref.current !== 'undefined' ? ref.current : null;
        if (window.IntersectionObserver) {
            const observer = new IntersectionObserver(([entry]) => {
                setIntersecting(entry.isIntersecting);
            }, {
                rootMargin,
                threshold,
            });
            if (ref === null || ref === void 0 ? void 0 : ref.current) {
                observer.observe(ref.current);
            }
            return () => {
                if (currentRef) {
                    observer.unobserve(currentRef);
                }
            };
        }
        return () => { };
    }, []);
    return isIntersecting;
}
export const AnimationContext = React.createContext({
    isOnScreen: false,
    moduleAnimatable: false,
    hasAnimated: 0,
});
export function useAnimations(contentType, shouldAnimate, threshold) {
    const animationRef = React.createRef();
    const [hasAnimated, setHasAnimated] = React.useState(0);
    const isOnScreen = useOnScreen(animationRef, '0px', threshold);
    const moduleAnimatable = shouldAnimate && isAnimatable(contentType);
    React.useEffect(() => {
        if (isOnScreen) {
            setHasAnimated(hasAnimated + 1);
        }
    }, [isOnScreen]);
    const animationContext = {
        isOnScreen,
        moduleAnimatable,
        hasAnimated,
    };
    return { animationContext, animationRef };
}
export function fadeInAnimation(delay) {
    return css `
    @media (min-width: ${screenMdMax}) {
      ${applyKeyframes};
      ${props => applyStartState(props)};
      ${props => applyAnimation(delay, props)};
    }
  `;
}
export function withAnimationContext(Component) {
    return (props) => {
        const animationContext = useContext(AnimationContext);
        return React.createElement(Component, { ...props, animationContext: animationContext });
    };
}
